/**
 * Robust local scan buffering — the V1 "at minimum" offline story spec
 * §13 explicitly allows in place of full offline mode: "If full offline
 * mode isn't in V1, implement robust local buffering at minimum and
 * document the limitation." Documented limitation: only SCANS are
 * buffered while offline (the hot, repeated, must-never-lose-a-scan
 * path per Rule 3/§9). Undo, manual quantity edits, exceptions, review,
 * and confirm all require a live connection and simply show an error if
 * attempted offline — those are deliberate, infrequent, human-reviewed
 * actions, not something to replay blind after a possibly-long gap.
 *
 * Each queued scan carries a client-generated correlationId, which the
 * backend's recordScan() already de-duplicates on (spec §38/§41,
 * ReceivingRepository.hasCorrelationId) — so a scan queued here, synced
 * later, and for whatever reason retried again, is never double-applied
 * server-side. This module is split into pure reducer functions (fully
 * unit-testable, no localStorage/network) and a thin persistence
 * wrapper (OfflineQueueStore) that's the only impure part.
 */

export interface QueuedScan {
  correlationId: string;
  sessionId: string;
  ean: string;
  deviceId?: string;
  queuedAt: string;
}

export function enqueue(queue: QueuedScan[], scan: QueuedScan): QueuedScan[] {
  return [...queue, scan];
}

export function dequeue(queue: QueuedScan[], correlationId: string): QueuedScan[] {
  return queue.filter((s) => s.correlationId !== correlationId);
}

/** Pending scans for one session, oldest first — a device could in principle hold queued scans from more than one session (e.g. switched ASN while offline). */
export function forSession(queue: QueuedScan[], sessionId: string): QueuedScan[] {
  return queue.filter((s) => s.sessionId === sessionId);
}

const STORAGE_KEY = 'wr.offlineScanQueue';

function readStorage(): QueuedScan[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    return Array.isArray(parsed) ? (parsed as QueuedScan[]) : [];
  } catch {
    // Corrupt/unavailable storage (private browsing, quota, bad JSON from
    // an older version) — treat as empty rather than crash the app; the
    // worst case is a handful of scans need re-entering, not a broken UI.
    return [];
  }
}

function writeStorage(queue: QueuedScan[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(queue));
  } catch {
    // Best-effort — see readStorage's comment.
  }
}

export type FlushResult = { synced: number; failed: number; offline: boolean };

/**
 * Attempts each queued scan for `sessionId`, in order, via `sendScan`
 * (normally api.scan). Stops at the first network failure (offline
 * again mid-flush) rather than reordering or skipping ahead — scans
 * must apply in the order they were made. A domain-level failure (the
 * request reached the server but was rejected) is logged and the scan
 * is still removed from the queue, since retrying an already-rejected
 * request forever would just wedge the queue; the operator sees the
 * pending count drop and can re-scan if something looks wrong on the
 * item list.
 */
export class OfflineQueueStore {
  private queue: QueuedScan[] = readStorage();

  list(sessionId?: string): QueuedScan[] {
    return sessionId ? forSession(this.queue, sessionId) : this.queue;
  }

  count(sessionId?: string): number {
    return this.list(sessionId).length;
  }

  add(scan: QueuedScan): void {
    this.queue = enqueue(this.queue, scan);
    writeStorage(this.queue);
  }

  async flush(
    sessionId: string,
    sendScan: (scan: QueuedScan) => Promise<{ networkError: boolean; ok: boolean }>,
  ): Promise<FlushResult> {
    let synced = 0;
    let failed = 0;
    for (const scan of forSession(this.queue, sessionId)) {
      const result = await sendScan(scan);
      if (result.networkError) {
        return { synced, failed, offline: true };
      }
      this.queue = dequeue(this.queue, scan.correlationId);
      writeStorage(this.queue);
      if (result.ok) synced++;
      else failed++;
    }
    return { synced, failed, offline: false };
  }
}
